import { canonicalDate, canonicalLiabilityKey } from './canonical'
import { parseAmount, parseDate, UUID_REGEX, validateModalParity } from './validate'
import { LiabilityType, ParsedCsvRow, PreparedLiabilityRow, RentRollRow } from './types'

const normalize = (value?: string | null) => (value ?? '').toLowerCase().replace(/\s+/g, ' ').trim()

const addressKey = (line1?: string | null) => normalize(line1)

const primaryTenantName = (row: RentRollRow) => {
  const user = row.rental?.rental_users?.[0]?.user
  return normalize(`${user?.first_name ?? ''} ${user?.last_name ?? ''}`)
}

const displayPrimaryTenantName = (row: RentRollRow) => {
  const user = row.rental?.rental_users?.[0]?.user
  return [user?.first_name, user?.last_name].filter(Boolean).join(' ')
}

const displayAddress = (row?: RentRollRow) =>
  [row?.display_line_1, row?.display_line_2].filter(Boolean).join(' ')

const rentRollRentalId = (row: RentRollRow): string | undefined =>
  row.rental?.id ?? row.id ?? undefined

const resolveRental = (
  row: ParsedCsvRow,
  rentRollRows: RentRollRow[]
): { rental?: RentRollRow; rentalId?: string; error?: string } => {
  const rentalId = row.raw.rental_id?.trim()
  if (rentalId) {
    if (!UUID_REGEX.test(rentalId)) {
      return { error: 'rental_id must be a valid UUID.' }
    }

    return {
      rental: rentRollRows.find((rentRollRow) => rentRollRentalId(rentRollRow) === rentalId),
      rentalId
    }
  }

  const address = row.raw.address?.trim()
  if (!address) {
    return { error: 'Provide rental_id or address.' }
  }

  const occupancyDate = row.raw.occupancy_date?.trim()
  const tenantName = row.raw.tenant_name?.trim()
  if (!occupancyDate && !tenantName) {
    return { error: 'Address rows require occupancy_date or tenant_name.' }
  }

  let canonicalOccupancyDate: string | undefined
  if (occupancyDate) {
    const occupancyDateResult = parseDate(occupancyDate, 'occupancy_date')
    if (occupancyDateResult.error) {
      return { error: occupancyDateResult.error }
    }
    canonicalOccupancyDate = occupancyDateResult.date
  }

  const key = addressKey(address)
  let candidates = rentRollRows.filter(
    (rentRollRow) => addressKey(rentRollRow.display_line_1) === key
  )

  if (occupancyDate && tenantName) {
    candidates = candidates.filter(
      (rentRollRow) =>
        rentRollRow.occupancy_date &&
        canonicalDate(rentRollRow.occupancy_date) === canonicalOccupancyDate &&
        primaryTenantName(rentRollRow) === normalize(tenantName)
    )
  } else if (occupancyDate) {
    candidates = candidates.filter(
      (rentRollRow) =>
        rentRollRow.occupancy_date &&
        canonicalDate(rentRollRow.occupancy_date) === canonicalOccupancyDate
    )
  } else if (tenantName) {
    candidates = candidates.filter(
      (rentRollRow) => primaryTenantName(rentRollRow) === normalize(tenantName)
    )
  }

  if (candidates.length === 0) {
    return { error: 'No matching rental found.' }
  }

  if (candidates.length > 1) {
    return { error: 'Multiple rentals match; use rental_id.' }
  }

  return {
    rental: candidates[0],
    rentalId: rentRollRentalId(candidates[0])
  }
}

const resolveLiabilityType = (
  row: ParsedCsvRow,
  liabilityTypes: LiabilityType[],
  defaultLiabilityTypeId?: string
): {
  liabilityType?: LiabilityType
  liabilityTypeId?: string
  error?: string
} => {
  const value = row.raw.liability_type?.trim()
  const fallbackId = defaultLiabilityTypeId || ''
  const liabilityTypeValue = value || fallbackId

  if (!liabilityTypeValue) {
    return { error: 'Select a liability type for blank rows.' }
  }

  const byId = liabilityTypes.find((liabilityType) => liabilityType.id === liabilityTypeValue)
  if (byId) {
    return {
      liabilityType: byId,
      liabilityTypeId: byId.id
    }
  }

  if (UUID_REGEX.test(liabilityTypeValue)) {
    return {
      liabilityTypeId: liabilityTypeValue
    }
  }

  const byName = liabilityTypes.filter(
    (liabilityType) => normalize(liabilityType.name) === normalize(liabilityTypeValue)
  )

  if (byName.length === 1) {
    return {
      liabilityType: byName[0],
      liabilityTypeId: byName[0].id
    }
  }

  if (byName.length > 1) {
    return { error: 'Liability type name matches multiple types.' }
  }

  return { error: 'Liability type not found.' }
}

export const prepareRows = ({
  defaultLiabilityTypeId,
  liabilityTypes,
  parsedRows,
  rentRollRows
}: {
  defaultLiabilityTypeId?: string
  liabilityTypes: LiabilityType[]
  parsedRows: ParsedCsvRow[]
  rentRollRows: RentRollRow[]
}): PreparedLiabilityRow[] => {
  const preparedRows = parsedRows.map((parsedRow): PreparedLiabilityRow => {
    const errors: string[] = []
    const rentalResult = resolveRental(parsedRow, rentRollRows)
    const liabilityTypeResult = resolveLiabilityType(
      parsedRow,
      liabilityTypes,
      defaultLiabilityTypeId
    )
    const amountResult = parseAmount(parsedRow.raw.amount)
    const dateResult = parseDate(parsedRow.raw.date)
    const note = parsedRow.raw.note?.trim() || null
    const externalNote = parsedRow.raw.external_note?.trim() || null

    for (const error of [
      rentalResult.error,
      liabilityTypeResult.error,
      amountResult.error,
      dateResult.error
    ]) {
      if (error) {
        errors.push(error)
      }
    }

    errors.push(
      ...validateModalParity({
        amount: amountResult.amount,
        date: dateResult.date,
        liabilityType: liabilityTypeResult.liabilityType,
        note,
        rental: rentalResult.rental
      })
    )

    return {
      ...parsedRow,
      amount: amountResult.amount,
      date: dateResult.date,
      errors,
      externalNote,
      liabilityType: liabilityTypeResult.liabilityType,
      liabilityTypeId: liabilityTypeResult.liabilityTypeId,
      note,
      rental: rentalResult.rental,
      rentalId: rentalResult.rentalId
    }
  })

  const indexesByKey = new Map<string, PreparedLiabilityRow[]>()
  for (const row of preparedRows) {
    if (
      row.errors.length ||
      !row.rentalId ||
      !row.liabilityTypeId ||
      !row.date ||
      row.amount === undefined
    ) {
      continue
    }

    const key = canonicalLiabilityKey({
      amount: row.amount,
      date: row.date,
      liabilityTypeId: row.liabilityTypeId,
      rentalId: row.rentalId
    })
    indexesByKey.set(key, [...(indexesByKey.get(key) ?? []), row])
  }

  for (const duplicateRows of indexesByKey.values()) {
    if (duplicateRows.length < 2) {
      continue
    }

    for (const row of duplicateRows) {
      const otherRow = duplicateRows.find((duplicateRow) => duplicateRow.index !== row.index)
      row.errors.push(`duplicate of CSV row ${otherRow?.rowNumber ?? duplicateRows[0].rowNumber}`)
    }
  }

  return preparedRows
}

export const rowAddress = displayAddress

export const rowTenantName = displayPrimaryTenantName
